import axios from 'axios';
import { ENV_CONFIG } from '../config/environment';
import { clearAllCompanyStorage } from '../utils/companyStorage';

// Crear instancia de axios configurada
const api = axios.create({
  baseURL: ENV_CONFIG.API_BASE_URL,
  timeout: ENV_CONFIG.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ENV_CONFIG.SANCTUM_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    if (error.response?.status === 401) {
      // Token expirado o inválido
      console.log('Token inválido, redirigiendo a login...');
      localStorage.removeItem(ENV_CONFIG.SANCTUM_TOKEN_KEY);
      localStorage.removeItem(ENV_CONFIG.USER_KEY);
      clearAllCompanyStorage();
      window.location.href = '/';
    } else if (error.response?.status === 403) {
      // Sin permisos
      console.log('Sin permisos para esta acción');
    }
    return Promise.reject(error);
  }
);

class EmpleadoService {
  // Obtener datos necesarios para el formulario (empresas, regímenes, AFPs)
  async getFormData() {
    try {
      console.log('🔍 Obteniendo datos para formulario de empleado...');
      const response = await api.get('/empleados/form-data');
      console.log('✅ Datos del formulario obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo datos del formulario:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al obtener datos del formulario');
    }
  }

  // Crear nuevo empleado
  async createEmpleado(empleadoData) {
    try {
      console.log('➕ Creando empleado:', empleadoData.nombreCompleto);
      console.log('📋 Datos enviados:', empleadoData);
      
      const response = await api.post('/empleados', empleadoData);
      console.log('✅ Empleado creado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando empleado:', error.response?.data || error.message);
      
      if (error.response?.status === 422) {
        // Errores de validación
        const errors = error.response.data?.errors || {};
        const firstError = Object.values(errors)[0];
        throw new Error(Array.isArray(firstError) ? firstError[0] : firstError || 'Datos de validación incorrectos');
      } else if (error.response?.status === 409) {
        // Conflicto - empleado ya existe
        throw new Error('Ya existe un empleado con este documento en la empresa');
      }
      
      throw new Error(error.response?.data?.message || 'Error al crear empleado');
    }
  }

  // Obtener todos los empleados
  async getAllEmpleados(params = {}) {
    try {
      console.log('🔍 Obteniendo lista de empleados...');
      const response = await api.get('/empleados', { params });
      console.log('✅ Empleados obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo empleados:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al obtener empleados');
    }
  }

  // Obtener empleado por ID
  async getEmpleadoById(id) {
    try {
      console.log('🔍 [empleadoService] Obteniendo empleado ID:', id);
      const response = await api.get(`/empleados/${id}`);
      
      // ===== LOGGING ESPECÍFICO PARA CHECKBOXES EN EL SERVICIO =====
      console.log('📊 [empleadoService] Respuesta completa del backend:', response);
      console.log('📊 [empleadoService] Datos del empleado desde backend:', response.data);
      
      if (response.data && response.data.data) {
        console.log('🔍 [empleadoService] CHECKBOXES EN LA RESPUESTA DEL BACKEND:', {
          asignacion_familiar: response.data.data.asignacion_familiar,
          tipo_asignacion_familiar: typeof response.data.data.asignacion_familiar,
          tiene_bono: response.data.data.tiene_bono,
          tipo_tiene_bono: typeof response.data.data.tiene_bono,
          monto_bono: response.data.data.monto_bono,
          tipo_monto_bono: typeof response.data.data.monto_bono,
          es_boleta_liquidada: response.data.data.es_boleta_liquidada,
          tipo_es_boleta_liquidada: typeof response.data.data.es_boleta_liquidada
        });
      }
      // ================================================================
      
      console.log('✅ [empleadoService] Empleado obtenido exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ [empleadoService] Error obteniendo empleado:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al obtener empleado');
    }
  }

  // Actualizar empleado
  async updateEmpleado(id, empleadoData) {
    try {
      console.log('✏️ Actualizando empleado ID:', id);
      console.log('📋 Datos enviados:', empleadoData);
      
      const response = await api.put(`/empleados/${id}`, empleadoData);
      console.log('✅ Empleado actualizado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando empleado:', error.response?.data || error.message);
      
      if (error.response?.status === 422) {
        // Errores de validación
        const errors = error.response.data?.errors || {};
        const firstError = Object.values(errors)[0];
        throw new Error(Array.isArray(firstError) ? firstError[0] : firstError || 'Datos de validación incorrectos');
      }
      
      throw new Error(error.response?.data?.message || 'Error al actualizar empleado');
    }
  }

  // Eliminar empleado
  async deleteEmpleado(id) {
    try {
      console.log('🗑️ Eliminando empleado ID:', id);
      const response = await api.delete(`/empleados/${id}`);
      console.log('✅ Empleado eliminado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando empleado:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al eliminar empleado');
    }
  }

  // Buscar empleados
  async buscarEmpleados(searchParams) {
    try {
      console.log('🔍 Buscando empleados con parámetros:', searchParams);
      const response = await api.get('/empleados/buscar', { params: searchParams });
      console.log('✅ Búsqueda completada:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error buscando empleados:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al buscar empleados');
    }
  }

  // Obtener historial laboral de un empleado
  async getHistorialLaboral(empleadoId) {
    try {
      console.log('📋 Obteniendo historial laboral del empleado ID:', empleadoId);
      const response = await api.get(`/empleados/${empleadoId}/historial-laboral`);
      console.log('✅ Historial laboral obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo historial laboral:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al obtener historial laboral');
    }
  }

  // Agregar registro al historial laboral
  async addHistorialLaboral(empleadoId, historialData) {
    try {
      console.log('➕ Agregando historial laboral al empleado ID:', empleadoId);
      const response = await api.post(`/empleados/${empleadoId}/historial-laboral`, historialData);
      console.log('✅ Historial laboral agregado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error agregando historial laboral:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al agregar historial laboral');
    }
  }

  // Crear registro de historial laboral (para bajas/altas)
  async createHistorialLaboral(historialData) {
    try {
      console.log('➕ Creando registro de historial laboral:', historialData);
      console.log('🌐 URL del endpoint:', '/empleados/historial-laboral');
      console.log('📤 Método:', 'POST');
      console.log('📋 Payload enviado:', JSON.stringify(historialData, null, 2));
      
      const response = await api.post('/empleados/historial-laboral', historialData);
      
      console.log('✅ Historial laboral creado exitosamente');
      console.log('📥 Respuesta del servidor:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando historial laboral:');
      console.error('🔴 Status:', error.response?.status);
      console.error('🔴 Data:', error.response?.data);
      console.error('🔴 Message:', error.message);
      throw new Error(error.response?.data?.message || 'Error al crear historial laboral');
    }
  }

  // Eliminar registro del historial laboral
  async deleteHistorialLaboral(empleadoId, historialId) {
    try {
      console.log('🗑️ Eliminando historial laboral:', { empleadoId, historialId });
      const response = await api.delete(`/empleados/${empleadoId}/historial-laboral/${historialId}`);
      console.log('✅ Historial laboral eliminado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando historial laboral:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al eliminar historial laboral');
    }
  }

  // Actualizar registro del historial laboral
  async updateHistorialLaboral(empleadoId, historialId, historialData) {
    try {
      console.log('✏️ Actualizando historial laboral:', { empleadoId, historialId, historialData });
      const response = await api.put(`/empleados/${empleadoId}/historial-laboral/${historialId}`, historialData);
      console.log('✅ Historial laboral actualizado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando historial laboral:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al actualizar historial laboral');
    }
  }

  async updateHistorialLaboralInterno(empleadoId, historialId, historialData) {
    try {
      const response = await api.put(
        `/empleados/${empleadoId}/historial-laboral-interno/${historialId}`,
        historialData
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al actualizar historial interno');
    }
  }

  async createHistorialLaboralInterno(historialData) {
    try {
      const response = await api.post('/empleados/historial-laboral-interno', historialData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al crear historial interno');
    }
  }

  async deleteHistorialLaboralInterno(empleadoId, historialId) {
    try {
      const response = await api.delete(
        `/empleados/${empleadoId}/historial-laboral-interno/${historialId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al eliminar historial interno');
    }
  }

  // Obtener conceptos de un empleado
  async getConceptos(empleadoId, tipo = null) {
    try {
      const endpoint = tipo ? `/empleados/${empleadoId}/conceptos/${tipo}` : `/empleados/${empleadoId}/conceptos`;
      console.log('💰 Obteniendo conceptos del empleado ID:', empleadoId, tipo ? `(${tipo})` : '');
      const response = await api.get(endpoint);
      console.log('✅ Conceptos obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo conceptos:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al obtener conceptos');
    }
  }

  // Agregar concepto a un empleado
  async addConcepto(empleadoId, conceptoData) {
    try {
      console.log('➕ Agregando concepto al empleado ID:', empleadoId);
      const response = await api.post(`/empleados/${empleadoId}/conceptos`, conceptoData);
      console.log('✅ Concepto agregado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error agregando concepto:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al agregar concepto');
    }
  }

  // Validar conexión con la API
  async checkApiConnection() {
    try {
      const response = await api.get('/empleados/form-data');
      return response.status === 200;
    } catch (error) {
      console.error('❌ Error verificando conexión API empleados:', error);
      return false;
    }
  }

  /**
   * Trabajadores activos próximos a cumplir N años de servicio.
   * @param {{ empresa_id: number|string, anios: number|string, dias_anticipacion?: number|string }} params
   */
  async getProximosAniversarios(params = {}) {
    try {
      const response = await api.get('/empleados/proximos-aniversarios', { params });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || 'Error al consultar próximos aniversarios laborales'
      );
    }
  }

  /**
   * Acopla personal activo existente al modo dual (fecha/sueldo interno desde datos actuales).
   */
  async acoplarPlanillaAlternativa(empresaId) {
    try {
      const response = await api.post('/empleados/acoplar-planilla-alternativa', {
        empresa_id: empresaId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || 'Error al acoplar personal a planilla alternativa'
      );
    }
  }
}

export default new EmpleadoService(); 